import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { hasAccessToFeature } from '../lib/planAccess';
import { LoadingSpinner } from './LoadingSpinner';

interface PlanGuardProps {
  children: React.ReactNode;
  requiredFeature: 'aiTutoring' | 'smartStudyPlan' | 'practiceTests' | 'calendar' | 'prioritySupport' | 'liveSessions';
  redirectTo?: string;
}

export const PlanGuard: React.FC<PlanGuardProps> = ({
  children,
  requiredFeature,
  redirectTo = '/planes'
}) => {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
          navigate('/login');
          return;
        }

        const { data: profile } = await supabase
          .from('parent_profiles')
          .select('subscription_plan')
          .eq('id', session.user.id)
          .single();

        if (!profile) {
          // Auto-create trial profile if it doesn't exist (covers edge function failures)
          console.log('⚡ No profile found, creating trial profile automatically...');
          const now = new Date();
          const trialEnd = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);

          const { error: insertError } = await supabase
            .from('parent_profiles')
            .insert({
              id: session.user.id,
              full_name: session.user.user_metadata?.full_name || 'User',
              email: session.user.email || '',
              phone: session.user.user_metadata?.phone || null,
              subscription_plan: 'trial',
              subscription_status: 'active',
              trial_start_date: now.toISOString(),
              trial_end_date: trialEnd.toISOString(),
            });

          if (insertError) {
            console.error('❌ Error creating profile:', insertError);
            navigate('/login');
            return;
          }

          console.log('✅ Trial profile created successfully');
          // Profile created with trial plan — grant access since trial has all features
          setHasAccess(true);
          setIsChecking(false);
          return;
        }

        const access = await hasAccessToFeature(requiredFeature);

        if (!access) {
          // Mostrar modal de actualización y redirigir
          setTimeout(() => {
            navigate(redirectTo);
          }, 100);
          return;
        }

        setHasAccess(true);
      } catch (error) {
        console.error('Error checking plan access:', error);
        navigate('/dashboard');
      } finally {
        setIsChecking(false);
      }
    };

    checkAccess();
  }, [navigate, requiredFeature, redirectTo]);

  if (isChecking) {
    return <LoadingSpinner />;
  }

  if (!hasAccess) {
    return null;
  }

  return <>{children}</>;
};
